import { RegistrationContainer } from '@bumble/app/components/RegistrationContainer';
import { MfaContext } from '@bumble/app/context';
import { useAccount } from '@bumble/app/hooks/use-account';
import { zodResolver } from '@hookform/resolvers/zod';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyIcon from '@mui/icons-material/Key';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Button, Divider, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { MouseEvent, useContext, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { TypeOf, object, string } from 'zod';
import FormInput from '../../components/FormInput';
import { useSubscriber } from '../../context/SubscriberContext';

const loginSchema = object({
  username: string().min(1, 'Email address is required').email('Email Address is invalid'),
  password: string()
    .min(1, 'Password is required')
    .min(8, 'Password must be more than 8 characters')
    .max(32, 'Password must be less than 32 characters'),
});

export type LoginInput = TypeOf<typeof loginSchema>;

type LoginObj = LoginInput & {
  subscriber?: string;
};

export const Login = () => {
  const mfaContext = useContext(MfaContext);
  if (mfaContext && mfaContext?.user) {
    mfaContext?.setUser(null);
  }
  const { login, isLoggingIn } = useAccount();
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const subscriberParam = searchParams.get('subscriber');
  const { subscriber, setSubscriber } = useSubscriber();

  useEffect(() => {
    if (subscriberParam) {
      setSubscriber(subscriberParam);
    }
  }, [setSubscriber, subscriberParam]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) =>
    void event.preventDefault();

  const methods = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const { reset, handleSubmit } = methods;

  const submitHandler: SubmitHandler<LoginObj> = (values) => {
    const loginObj = { ...values };
    if (subscriber) {
      loginObj.subscriber = subscriber;
    }
    login(loginObj);
    reset();
  };

  return (
    <RegistrationContainer>
      <Typography variant="body1" component="p" py={2} mb={2}>
        Welcome back! Please enter your details.
      </Typography>
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(submitHandler)} noValidate autoComplete="off">
          <FormInput
            id="username"
            name="username"
            label="Email Address"
            type="text"
            startAdornment={
              <InputAdornment position="start">
                <EmailOutlinedIcon />
              </InputAdornment>
            }
          />
          <FormInput
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            startAdornment={
              <InputAdornment position="start">
                <KeyIcon />
              </InputAdornment>
            }
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" my={2}>
            <Box />
            <Button
              size="small"
              variant="text"
              color="secondary"
              component={Link}
              to="/forgot-password"
            >
              Forgot password?
            </Button>
          </Stack>
          <Box mt={4}>
            <Button variant="contained" size="large" fullWidth type="submit" disabled={isLoggingIn}>
              Login
            </Button>
          </Box>
        </Box>
      </FormProvider>
      <Box py={6}>
        <Divider />
      </Box>
      <Button size="small" variant="text" component={Link} to="/register">
        Don&apos;t have an account?
      </Button>
    </RegistrationContainer>
  );
};
